/**
 * `/api/exports/full-backup`, `/api/exports/filtered-view` and
 * `/api/exports/:jobId` (Requirements 14, 15, R18.4, R18.17).
 *
 * Exports are jobs, not responses: generation streams to a temporary file and the
 * file is exposed only on success, so a failure delivers nothing at all (R14.11,
 * R15.9). The job shape carries that contract - a failed job has no download token
 * and states that no file was produced - and it carries the progress state the
 * client polls at least every five seconds (R18.4), with termination at 300 seconds
 * (R18.17).
 *
 * The filtered-view request carries the *same* filter representation the screen
 * used, which is what makes R13.6's zero-additional and zero-omitted guarantee a
 * consequence of running the identical query rather than of a parallel export query.
 *
 * The record cap of R14.9 is configuration, checked before generation; exceeding it
 * is reported as a validation finding carrying the requested and configured counts,
 * so no schema here restates the number.
 *
 * Both requests and the status poll answer with an {@link exportJobSchema}.
 */
import { z } from 'zod';
import { calendarOrientationSchema } from './enums.js';
import { appliedFilterDimensionSchema, initiativeFilterSchema, sortOrderSchema } from './filters.js';
// The governance-metrics export takes the dashboard's own request shape, so the two
// cannot describe different scopes (`complan-governance` R1.11).
import { governanceScopeSchema } from './governance.js';
import { idSchema, planningDateSchema, timestampSchema } from './primitives.js';
/**
 * The export formats: Foundation's two, plus `complan-governance` R3.13's.
 *
 * `governance_metrics` exports *figures*, not records, which is why it is a third kind
 * rather than a third `ExportSource` of the filtered view: a filtered-view export is one
 * Initiative per row under a filter, and a metrics export is one metric per row under a
 * filter *and a cut-off date*. Reusing the filtered-view request would have meant a
 * `source` that changed what the rows are, and an optional cut-off date that only one
 * source reads.
 */
export const EXPORT_KINDS = Object.freeze([
    'full_backup',
    'filtered_view',
    'governance_metrics',
]);
/** See {@link EXPORT_KINDS}. */
export const exportKindSchema = z.enum(EXPORT_KINDS);
/** Where a filtered-view export was requested from, which decides its columns (R15.1, R15.7). */
export const EXPORT_SOURCES = Object.freeze(['calendar', 'initiative_list']);
/** See {@link EXPORT_SOURCES}. */
export const exportSourceSchema = z.enum(EXPORT_SOURCES);
/** The life cycle of an export job. */
export const EXPORT_JOB_STATES = Object.freeze([
    'queued',
    'running',
    'succeeded',
    'failed',
    'timed_out',
]);
/** See {@link EXPORT_JOB_STATES}. */
export const exportJobStateSchema = z.enum(EXPORT_JOB_STATES);
/**
 * `POST /api/exports/full-backup` (R14.1).
 *
 * No filters: the full backup covers everything the requesting user may read, and
 * the permission scope it was generated under is recorded in the workbook (R14.5).
 */
export const fullBackupExportRequestSchema = z.object({});
/**
 * `POST /api/exports/filtered-view` (R15.1).
 *
 * `columns` is populated only for an export from an Initiative list, where R15.7
 * exports the visible columns in their display order plus the Initiative identifier
 * when the list omits it. A calendar export uses the 21 columns R15.1 names, so it
 * sends none.
 */
export const filteredViewExportRequestSchema = z.object({
    source: exportSourceSchema,
    filters: initiativeFilterSchema,
    orientation: calendarOrientationSchema,
    sort: sortOrderSchema,
    columns: z.array(z.string().min(1)).default([]),
});
/**
 * One metric's display text, as the screen that requested the export renders it
 * (`complan-governance` R2.8, R2.10).
 *
 * The figures in a metrics export are the server's; the *words* are the client's, and
 * deliberately so. R2.8 requires a Metric_Definition to travel through the localization
 * mechanism, and the catalogue lives in the web package - the API has no i18n resources
 * at all, which is why the other two exports carry hard-coded English column headers.
 * Having the requesting screen send the text it displayed makes the export say exactly
 * what the reader saw, in the language they saw it in, with no second catalogue to drift.
 *
 * It is optional per metric: a metric the client says nothing about is written with the
 * registry's own identifiers, so the sheet is never missing a row and a client that sends
 * nothing still gets a complete, if terse, export.
 */
export const exportedMetricLabelSchema = z.object({
    metricId: z.string().min(1).max(80),
    /** The metric's name, as the tile's heading reads it. */
    label: z.string().min(1).max(160),
    /** What the numerator counts (R2.4). */
    counts: z.string().min(1).max(400),
    /** What the denominator counts, for a share; null for a count or an hours total (R2.3). */
    outOf: z.string().min(1).max(400).nullable().default(null),
    /** The exclusions, one sentence each (R2.5-R2.7). */
    excludes: z.array(z.string().min(1).max(400)).max(8).default([]),
});
/**
 * `POST /api/exports/governance-metrics` (`complan-governance` R3.13).
 *
 * The same scope the dashboard itself was computed under - the Filter_Context and the
 * cut-off date - so R1.11's "the identical Readable_Set and filter selection the screen
 * applied" is a consequence of running the same request, not of a parallel export query.
 */
export const governanceMetricsExportRequestSchema = governanceScopeSchema.extend({
    /** See {@link exportedMetricLabelSchema}. */
    labels: z.array(exportedMetricLabelSchema).max(64).default([]),
});
/** `/api/exports/:jobId`. */
export const exportJobParamsSchema = z.object({ jobId: idSchema });
/**
 * The metadata block written into the workbook and echoed to the client (R14.5,
 * R15.3), including every filter dimension with the unfiltered ones marked as such.
 */
export const exportMetadataSchema = z.object({
    generatedAt: timestampSchema,
    exportedByUserId: idSchema,
    /** R14.5: the schema version of the exported layout. */
    exportSchemaVersion: z.string().min(1),
    /** R14.5, R15.3: the permission scope the export was produced under. */
    permissionScope: z.string().min(1),
    appliedFilters: z.array(appliedFilterDimensionSchema).default([]),
    /**
     * The date the exported figures were evaluated against
     * (`complan-governance` R3.13, R8.2).
     *
     * Set by the governance-metrics export and null for the other two, which evaluate
     * nothing against a date. Carried *beside* `appliedFilters` rather than inside it,
     * because a cut-off date is not a filter dimension: it does not narrow the record set,
     * it decides what "overdue" means over that set. Filing it as a filter would make the
     * metadata sheet claim a dimension the Filter_Context does not have.
     */
    cutOffDate: planningDateSchema.nullable().default(null),
});
/**
 * An export job.
 *
 * `downloadToken` is present only in the `succeeded` state, which is the wire form
 * of "no partial file is ever delivered": a failed or timed-out job has nothing to
 * download and says so through `state` alone, never through a token that would 404.
 */
export const exportJobSchema = z.object({
    jobId: idSchema,
    kind: exportKindSchema,
    state: exportJobStateSchema,
    requestedAt: timestampSchema,
    completedAt: timestampSchema.nullable().default(null),
    /** Progress for the polling client (R18.4), 0 to 100. */
    progressPercent: z.number().int().min(0).max(100),
    /** Exported Initiative record count; zero is a valid result (R14.12, R15.4). */
    initiativeCount: z.number().int().min(0).nullable().default(null),
    downloadToken: z.string().min(1).nullable().default(null),
    metadata: exportMetadataSchema.nullable().default(null),
});
//# sourceMappingURL=export.js.map